"use client";

import { motion, useTransform } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "./axis-label-budget";
import {
  axisLabelFlowOffsets,
  CHART_X_AXIS_ROW_CLASS,
  useChartAxisSlots,
} from "./chart-axis-frame";
import { useChart, useChartStable } from "./chart-context";
import {
  ProjectedAxisLabels,
  useChartProjection,
  useProjectionProgress,
} from "./projection";

export interface BarXAxisProps {
  /** Width of the date ticker box for fade calculation. Default: 50 */
  tickerHalfWidth?: number;
  /** Whether to show all labels or skip some for dense data. Default: true */
  showAllLabels?: boolean;
  /** Maximum number of labels to show when `showAllLabels` is false. Default: 12 */
  maxLabels?: number;
}

const LABEL_GUTTER = 6;
const LABEL_CLASS =
  "text-center text-chart-label text-xs leading-tight [overflow-wrap:anywhere]";

interface BarXAxisLabelProps {
  label: string;
  x: number;
  bandWidth: number;
  crosshairX: number | null;
  isHovering: boolean;
  tickerHalfWidth: number;
  /** Flow layout: box is a flex item offset by margin. Overlay: absolute. */
  offsetLeft: number | null;
}

function BarXAxisLabel({
  label,
  x,
  bandWidth,
  crosshairX,
  isHovering,
  tickerHalfWidth,
  offsetLeft,
}: BarXAxisLabelProps) {
  const fadeBuffer = 20;
  const fadeRadius = tickerHalfWidth + fadeBuffer;

  let opacity = 1;
  if (isHovering && crosshairX !== null) {
    const distance = Math.abs(x - crosshairX);
    if (distance < tickerHalfWidth) {
      opacity = 0;
    } else if (distance < fadeRadius) {
      opacity = (distance - tickerHalfWidth) / fadeBuffer;
    }
  }

  const maxWidth = axisLabelMaxWidth("xAxisTick", bandWidth - LABEL_GUTTER);

  return (
    <div
      className={
        offsetLeft === null
          ? "absolute flex justify-center"
          : "flex shrink-0 justify-center"
      }
      style={
        offsetLeft === null
          ? { left: x - bandWidth / 2, bottom: 12, width: bandWidth }
          : { marginLeft: offsetLeft, width: bandWidth }
      }
    >
      <motion.span
        animate={{ opacity }}
        className={LABEL_CLASS}
        initial={{ opacity: 1 }}
        style={{ maxWidth }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {label}
      </motion.span>
    </div>
  );
}

export function BarXAxis(props: BarXAxisProps) {
  const { containerRef, barScale } = useChartStable();
  const slots = useChartAxisSlots();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const target = slots?.xRail ?? containerRef.current;
  if (!(mounted && target)) {
    return null;
  }

  if (!barScale) {
    return null;
  }

  return (
    <BarXAxisInner
      {...props}
      framed={slots?.xRail != null}
      target={target}
    />
  );
}

const BarXAxisInner = memo(function BarXAxisInner({
  tickerHalfWidth = 50,
  showAllLabels = true,
  maxLabels = 12,
  framed,
  target,
}: BarXAxisProps & { framed: boolean; target: HTMLElement }) {
  const { margin, tooltipData, barScale, bandWidth, barXAccessor, data } =
    useChart();
  const projection = useChartProjection();
  const projectionProgress = useProjectionProgress(projection);
  const htmlOpacity = useTransform(projectionProgress, [0, 0.4], [1, 0]);

  const labelsToShow = useMemo(() => {
    if (!(barScale && bandWidth && barXAccessor)) {
      return [];
    }

    const allLabels = data.map((d) => {
      const label = barXAccessor(d);
      const bandX = barScale(label) ?? 0;
      const x = bandX + bandWidth / 2 + margin.left;
      return { label, x };
    });

    return showAllLabels || allLabels.length <= maxLabels
      ? allLabels
      : allLabels.filter(
          (_, i) => i % Math.ceil(allLabels.length / maxLabels) === 0
        );
  }, [
    barScale,
    bandWidth,
    barXAccessor,
    data,
    margin.left,
    showAllLabels,
    maxLabels,
  ]);

  const isHovering = tooltipData !== null;
  const crosshairX = tooltipData ? tooltipData.x + margin.left : null;
  const boxWidth = bandWidth ?? 0;

  const flowOffsets = framed
    ? axisLabelFlowOffsets(
        labelsToShow.map((item) => item.x),
        boxWidth
      )
    : null;

  const labels = labelsToShow.map((item, index) => (
    <BarXAxisLabel
      bandWidth={boxWidth}
      crosshairX={crosshairX}
      isHovering={isHovering}
      key={`${item.label}-${item.x}`}
      label={item.label}
      offsetLeft={flowOffsets?.[index] ?? null}
      tickerHalfWidth={tickerHalfWidth}
      x={item.x}
    />
  ));

  const rootClassName = framed
    ? cn("pointer-events-none", CHART_X_AXIS_ROW_CLASS)
    : "pointer-events-none absolute inset-0";

  if (!projection) {
    return createPortal(<div className={rootClassName}>{labels}</div>, target);
  }

  return (
    <>
      {createPortal(
        <motion.div className={rootClassName} style={{ opacity: htmlOpacity }}>
          {labels}
        </motion.div>,
        target
      )}
      <ProjectedAxisLabels
        axis="x"
        labels={labelsToShow.map((item) => ({
          key: `${item.label}-${item.x}`,
          text: item.label,
          x: item.x - margin.left,
          y: projection.frame.depth,
          z: 0,
        }))}
      />
    </>
  );
});

BarXAxis.displayName = "BarXAxis";

export default BarXAxis;
